import React from 'react';
import { AlertCircle, Inbox, Loader2 } from 'lucide-react';
import { PageContainer, type PageWidth } from '@/components/layout/PageContainer';

interface PageStateProps {
  kind: 'loading' | 'error' | 'empty';
  title?: string;
  description?: string;
  width?: PageWidth;
  action?: React.ReactNode;
  className?: string;
}

export const PageState: React.FC<PageStateProps> = ({
  kind,
  title,
  description,
  width = 'default',
  action,
  className,
}) => {
  const content =
    kind === 'loading'
      ? {
          icon: <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />,
          title: title ?? 'Loading',
          description: description ?? 'Please wait while we load your data.',
          tone: 'text-muted-foreground border-border bg-background',
        }
      : kind === 'error'
        ? {
            icon: <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />,
            title: title ?? 'Something went wrong',
            description: description ?? 'Please try again.',
            tone: 'text-destructive border-destructive/20 bg-destructive/5',
          }
        : {
            icon: <Inbox className="h-6 w-6 text-muted-foreground" aria-hidden="true" />,
            title: title ?? 'No data found',
            description: description ?? 'There is nothing to show yet.',
            tone: 'text-muted-foreground border-border bg-background',
          };

  return (
    <PageContainer width={width} className={className}>
      <div className="min-h-[12rem] flex items-center justify-center">
        <div className={`w-full max-w-xl rounded-lg border p-4 sm:p-5 ${content.tone}`}>
          <div className="flex items-start gap-3">
            {content.icon}
            <div className="min-w-0">
              <p className="text-sm font-semibold">{content.title}</p>
              <p className="text-sm mt-1">{content.description}</p>
              {action && <div className="mt-3">{action}</div>}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};
